// ── run.js ────────────────────────────────────────────────────────────────────
// The math behind "The Run" — a self-paced binge plan for shows that are already
// fully available (a Netflix-style full drop, or just a backlog you're behind on),
// where there's no air schedule to plan against so YOU set the pace.
//
// A run stores a goal (a nightly PACE or a finish-by DEADLINE); everything else —
// how many episodes are left, tonight's suggested slice, whether you're on pace,
// the projected finish — is DERIVED from your live progress so it self-adjusts.
//
// Reuses the canonical season/episode math in episode.js.
import { getValidSeasons, countWatchedEpisodes, effectiveLastAired, nextProgressAfter, compareProgress } from './episode'

const DAY = 86400000
const dayDiff = (aStr, bStr) => Math.round((new Date(aStr + 'T00:00:00') - new Date(bStr + 'T00:00:00')) / DAY)

// Episodes the user has actually watched (progress is the NEXT-to-watch marker,
// so watched = everything strictly before it).
function watchedCount(progress, seasons) {
  if (!progress?.season) return 0
  const before = { season: progress.season, episode: Math.max(0, (progress.episode || 1) - 1) }
  return countWatchedEpisodes(before, seasons)
}

// How many unwatched episodes are ALREADY available right now (between the user's
// progress and the last-aired/finale marker). This is the bingeable backlog.
export function availableRemaining(show, progress, todayStr) {
  const seasons = getValidSeasons(show?.seasons)
  if (!seasons.length) return 0
  const through = effectiveLastAired(show, todayStr)
  if (!through) return 0
  const availableTotal = countWatchedEpisodes(through, seasons)
  return Math.max(0, availableTotal - watchedCount(progress, seasons))
}

// Worth offering a Run? A backlog of at least a few already-available episodes.
export function isRunnable(show, progress, todayStr, min = 3) {
  return availableRemaining(show, progress, todayStr) >= min
}

// Advance a { season, episode } marker forward n steps, honoring season boundaries.
function advance(marker, n, seasons) {
  let m = { ...marker }
  for (let i = 0; i < n; i++) m = nextProgressAfter({ season: m.season, episode: m.episode }, seasons)
  return m
}

// Label for tonight's slice, e.g. "S2·E5–E7" or "S2·E5 → S3·E1" or "S2·E5".
function sliceLabel(start, count, seasons) {
  if (count <= 1) return `S${start.season}·E${start.episode}`
  const end = advance(start, count - 1, seasons)
  if (end.season === start.season) return `S${start.season}·E${start.episode}–E${end.episode}`
  return `S${start.season}·E${start.episode} → S${end.season}·E${end.episode}`
}

// ── runStatus ─────────────────────────────────────────────────────────────────
// The single derived view of a run. Pass the run row, the show, the user's live
// progress { season, episode }, and today's key (YYYY-MM-DD).
//
// run: { mode:'pace'|'deadline', pace, target_date, remaining_at_start, started_at(YYYY-MM-DD) }
export function runStatus(run, show, progress, todayStr) {
  const seasons   = getValidSeasons(show?.seasons)
  const remaining = availableRemaining(show, progress, todayStr)
  const startRemaining = run.remaining_at_start ?? remaining
  const watchedSinceStart = Math.max(0, startRemaining - remaining)

  // Effective nightly pace. Deadline mode recomputes from what's left and the
  // days remaining, so it adapts as you get ahead or fall behind.
  let pace
  let daysToDeadline = null
  if (run.mode === 'deadline' && run.target_date) {
    daysToDeadline = Math.max(0, dayDiff(run.target_date, todayStr))
    pace = remaining > 0 ? Math.max(1, Math.ceil(remaining / Math.max(1, daysToDeadline + 1))) : 0
  } else {
    pace = Math.max(1, run.pace || 1)
  }

  const sliceCount = Math.min(pace, remaining)
  let slice = null
  if (sliceCount > 0) {
    const episodes = []
    let m = { season: progress.season, episode: progress.episode }
    for (let i = 0; i < sliceCount; i++) { episodes.push(m); m = nextProgressAfter(m, seasons) }
    slice = { count: sliceCount, label: sliceLabel(progress, sliceCount, seasons), episodes, endMarker: episodes[episodes.length - 1] }
  }

  // Projected finish (calendar date) at the current effective pace.
  const daysLeft = pace > 0 ? Math.ceil(remaining / pace) : 0
  const projectedFinish = remaining > 0
    ? new Date(new Date(todayStr + 'T00:00:00').getTime() + daysLeft * DAY).toISOString().slice(0, 10)
    : todayStr

  // Standing: how many you've watched vs how many the plan expected by today.
  // Day 0 = the start day; by the end of day N you'd expect pace*(N+1).
  let standing = 'on', diff = 0
  if (remaining > 0 && run.started_at) {
    const elapsed = Math.max(0, dayDiff(todayStr, run.started_at))  // full days since start
    if (run.mode === 'deadline' && run.target_date) {
      const totalDays = Math.max(1, dayDiff(run.target_date, run.started_at) + 1)
      const expected  = Math.min(startRemaining, Math.round(startRemaining * Math.min(1, elapsed / totalDays)))
      diff = watchedSinceStart - expected
    } else {
      const expected = pace * elapsed   // by the START of today you "owe" pace per full day elapsed
      diff = watchedSinceStart - expected
    }
    // The start day is grace — don't label ahead/behind until a full day has passed.
    standing = elapsed === 0 ? 'on' : diff >= pace ? 'ahead' : diff <= -pace ? 'behind' : 'on'
  }

  const done = remaining === 0
  return { remaining, startRemaining, watchedSinceStart, pace, slice, projectedFinish, daysToDeadline, standing, diff, done }
}

// Suggest a sensible default finish date for the deadline picker (≈3/night).
export function suggestFinishDate(show, progress, todayStr) {
  const remaining = availableRemaining(show, progress, todayStr)
  const days = Math.max(1, Math.ceil(remaining / 3))
  return new Date(new Date(todayStr + 'T00:00:00').getTime() + (days - 1) * DAY).toISOString().slice(0, 10)
}
